'use client';

import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import { useIsViewingAsClient } from '@/@subsidiary/hooks/useIsViewingAsClient';
import { viewAsListScope } from '@/@subsidiary/view-as-scope';
import { createGraphQLService } from '@/lib/http/graphql';
import { tracking } from '@/lib/features/tracking';
import { useSession } from '@/providers/enhanced-session';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffect, useMemo, useState } from 'react';
import { toProjectRef } from './project-ref';

const graphqlRequest = createGraphQLService({
    serviceName: 'ReportsProjectService',
    featureName: 'Reports',
});

/**
 * Deliberately narrower than `@/@projects/queries/projects`'s
 * GET_SALESFORCE_PROJECTS, which also pulls `totalRespondentCount`,
 * `totalVerifiedRespondentCount`, and per-survey names, counts and media flags.
 *
 * Those are aggregates, and asking for them across an unfiltered page of
 * projects timed out at 15s in test. The only survey field the picker needs is
 * the id — that is the crosstab route's scope — so everything else is dropped.
 * The shared query keeps its extra fields for callers that render them; this one
 * is for populating a dropdown.
 */
const GET_REPORT_PROJECT_OPTIONS = gql`
    query ReportProjectOptions(
        $sort: [SortInput!]
        $pagination: PaginationInput
        $filter: SalesforceProjectWhereInput
    ) {
        salesforceProjects(sort: $sort, pagination: $pagination, filter: $filter) {
            items {
                id
                projectName
                accountName
                accountId
                surveys {
                    id
                }
            }
            total
        }
    }
`;

interface ProjectOptionsResponse {
    salesforceProjects: {
        items: {
            id: string;
            projectName: string;
            accountName: string;
            accountId: string;
            surveys?: { id: string }[];
        }[];
        total: number;
    };
}

/**
 * The "still in field" variant needs to know fielding state, and
 * `salesforceProjects` has none to give — `SalesforceProjectEntity` exposes no
 * status or date field at all (probed 2026-08-25, and still true 2026-09-01).
 * The top-level `surveys` query does: `isActive` marks a survey currently in
 * field (see the portal's `SurveyItem` type), and it filters server-side. So
 * in-field mode inverts the shape — fetch the live surveys, then group them
 * back into project options.
 *
 * No project-name fields here: probed against test1 2026-09-01, the live
 * schema has neither `SurveyEntity.projectName` nor
 * `SurveyWhereInput.projectName` (the generated graphql.ts is stale on both).
 * The name comes from the nested `salesforceProject`, and search filters
 * client-side — the in-field set is small and fetched whole anyway.
 *
 * And no `isActive` in the WHERE, deliberately: the input type is Boolean but
 * the column is a smallint, and the API passes the boolean through untranslated
 * — Postgres rejects it (`invalid input syntax for type smallint: "true"`,
 * observed against test1 2026-09-01; the fix belongs in mfour-studio-api's
 * filter mapping). The fielding-date window filters fine server-side, so it
 * carries the WHERE and `isActive` is re-checked client-side from the
 * selection set, where it's just a field read.
 */
const GET_IN_FIELD_PROJECT_OPTIONS = gql`
    query InFieldProjectOptions($where: SurveyWhereInput) {
        surveys(where: $where) {
            id
            isActive
            salesforceProject {
                id
                projectName
                accountName
                accountId
            }
        }
    }
`;

interface InFieldSurveysResponse {
    surveys: {
        id: string;
        isActive: boolean;
        salesforceProject: {
            id: string;
            projectName: string;
            accountName: string | null;
            accountId: string | null;
        } | null;
    }[];
}

/**
 * One page of projects. Matches the limit the crosstab context already uses
 * successfully; more can be loaded, and search is server-side, so this is a page
 * size rather than a ceiling on what the user can reach.
 */
const PAGE_SIZE = 50;

/**
 * Sorting is by name descending, which is newest-first in practice: stored
 * project names begin `YYYY-MM-`, so lexical order is chronological order.
 *
 * That is a property of the naming convention, not of the schema — probed
 * 2026-08-25, `SalesforceProjectEntity` exposes no date field at all (nor
 * `createdAt`, `startDate`, or `status`), and `SurveyDto` has only `id`, `name`,
 * `respondentCount`, `verifiedRespondentCount`, `hasImages`, `hasVideos`. Roughly
 * a third of stored names carry no date prefix; those sort alphabetically among
 * the dated ones. Search is the answer for those, not sort order.
 */
const SORT = [{ direction: 'DESC', field: 'projectName' }];

/**
 * Neither `GraphQLClient` nor React Query times a request out on its own, and
 * `getSessionToken` awaits a session fetch before the request is even made. Any
 * of those stalling leaves the picker spinning with nothing to show and no way
 * to recover — so bound the whole thing and turn a hang into a readable error.
 */
const REQUEST_TIMEOUT_MS = 30_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error(`Timed out after ${Math.round(ms / 1000)}s`)),
            ms,
        );
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });
}

export interface ProjectOption {
    /** Salesforce project id. Stable, but not what the planner wants. */
    id: string;
    /** Full stored name, shown to the user — what they recognise. */
    projectName: string;
    /** `NNNNN-Name`, the reference the planner receives. */
    ref: string;
    accountName: string;
    /** Salesforce account id. Account-wide scope — too broad for a crosstab. */
    accountId: string;
    /**
     * Survey ids in this project. The crosstab route's preferred scope
     * (`?surveys=`); its own comment warns that account-level scope produces
     * "huge, wrong crosstabs", so always send these rather than accountId.
     */
    surveyIds: number[];
}

/**
 * The SF accounts the picker is scoped to while a staff member views as a
 * client (M4S-5670 §7): the client's own, so a report is only ever set up on
 * the client's projects. Null outside View-as, where the picker lists what the
 * caller can see, as it always has.
 *
 * Under View-as the ids arrive a moment after the first render. Until they do
 * the scope is not ready and nothing is fetched, as `@/@projects/context` does
 * for My Surveys: an empty filter would list every client's projects.
 */
export function viewAsProjectScope(
    isViewingAsClient: boolean,
    selectedSfAccountIds: readonly string[],
): { ready: boolean; sfAccountIds: readonly string[] | null } {
    const scope = viewAsListScope({
        isViewingAsClient,
        sfAccountIds: selectedSfAccountIds,
        orgIds: [],
    });
    return { ready: scope.sfReady, sfAccountIds: scope.sfAccountIds };
}

/** The `salesforceProjects` filter for a scope, matching `@/@projects/context`. */
export function sfAccountFilter(sfAccountIds: readonly string[] | null): {
    sfAccountId?: { eq: string } | { in: string[] };
} {
    if (!sfAccountIds || sfAccountIds.length === 0) return {};
    if (sfAccountIds.length === 1) return { sfAccountId: { eq: sfAccountIds[0] } };
    return { sfAccountId: { in: [...sfAccountIds] } };
}

/** Keeps the options whose account is in scope; all of them when there is no scope. */
export function withinAccountScope(
    options: ProjectOption[],
    sfAccountIds: readonly string[] | null,
): ProjectOption[] {
    if (!sfAccountIds) return options;
    return options.filter((option) => sfAccountIds.includes(option.accountId));
}

/**
 * The projects this user can see, shaped for the builder's project picker.
 *
 * Exists because a project reference cannot be resolved from what a user types:
 * the number lives inside the project name and matches no id column. Picking
 * from this list is the only way the builder ends up holding both the string the
 * planner needs and the survey ids the topline preview and question picker need.
 */
/**
 * @param search Free text matched server-side against the project name via
 *   `StringFilterInput.contains` (probed available alongside `eq`, `startsWith`
 *   and `in`). Searching on the server rather than filtering a page client-side
 *   is what lets a user reach a project beyond the first page at all.
 */
export function useProjectOptions(
    search: string,
    {
        inFieldOnly = false,
    }: {
        /**
         * List only projects with a survey currently in field (see
         * GET_IN_FIELD_PROJECT_OPTIONS). In this mode the whole result set
         * arrives at once — `hasMore` is always false — and a picked option's
         * `surveyIds` carry the in-field surveys only, which is the scope a
         * mid-field read should analyze.
         */
        inFieldOnly?: boolean;
    } = {},
): {
    options: ProjectOption[];
    /** Total matching the current search, server-side. */
    total: number;
    /** A request is in flight. */
    isFetching: boolean;
    /**
     * Session not ready yet, or (under View-as) the client's scope not ready
     * yet, so the query has not started.
     */
    isWaitingForSession: boolean;
    hasMore: boolean;
    loadMore: () => void;
    error: Error | null;
    retry: () => void;
} {
    const { data: session } = useSession();
    const isViewingAsClient = useIsViewingAsClient();
    const selectedSfAccountIds =
        useSubsidiaryFilterOptional()?.selectedSfAccountIds ?? EMPTY_SF_ACCOUNT_IDS;
    const scope = viewAsProjectScope(isViewingAsClient, selectedSfAccountIds);
    const scopeKey = scope.sfAccountIds ? scope.sfAccountIds.join(',') : 'all-accounts';
    const isWaitingForSession = !session?.user?.id || !scope.ready;

    const term = search.trim();
    const [limit, setLimit] = useState(PAGE_SIZE);

    // A new search starts from one page again — otherwise a previously expanded
    // list would make every later search fetch far more than it needs.
    useEffect(() => {
        setLimit(PAGE_SIZE);
    }, [term]);

    const { data, isFetching, error, refetch } = useQuery({
        // In-field mode fetches the whole (small) set once and searches it
        // client-side — the server has no project-name filter to give (see
        // GET_IN_FIELD_PROJECT_OPTIONS) — so its key carries no term. Both keys
        // carry the View-as scope, so one client's list is never shown as another's.
        queryKey: inFieldOnly
            ? ['reports', 'project-options', 'in-field', scopeKey]
            : ['reports', 'project-options', 'all', scopeKey, term, limit],
        enabled: !isWaitingForSession,
        staleTime: 5 * 60 * 1000,
        retry: false,
        // Keeps the current list on screen while a new search resolves, instead
        // of flashing empty on every keystroke.
        placeholderData: (previous) => previous,
        queryFn: () =>
            withTimeout(
                inFieldOnly
                    ? fetchInFieldProjectOptions().then(({ options }) => {
                          const scoped = withinAccountScope(options, scope.sfAccountIds);
                          return { options: scoped, total: scoped.length };
                      })
                    : fetchProjectOptionsPage(term, limit, scope.sfAccountIds),
                REQUEST_TIMEOUT_MS,
            ).catch((err: unknown) => {
                const error = err instanceof Error ? err : new Error(String(err));
                tracking.errorWithSource(error, 'useProjectOptions');
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[reports] project list failed:', error.message, error);
                }
                throw error;
            }),
    });

    const allOptions = data?.options ?? EMPTY_OPTIONS;
    const options = useMemo(() => {
        if (!inFieldOnly || !term) return allOptions;
        const needle = term.toLowerCase();
        return allOptions.filter(
            (option) =>
                option.projectName.toLowerCase().includes(needle) ||
                option.accountName.toLowerCase().includes(needle),
        );
    }, [allOptions, inFieldOnly, term]);
    const total = inFieldOnly ? options.length : (data?.total ?? 0);

    return {
        options,
        total,
        isFetching,
        isWaitingForSession,
        hasMore: !inFieldOnly && options.length < total,
        loadMore: () => setLimit((current) => current + PAGE_SIZE),
        error: (error as Error) ?? null,
        retry: () => void refetch(),
    };
}

const EMPTY_OPTIONS: ProjectOption[] = [];
const EMPTY_SF_ACCOUNT_IDS: readonly string[] = [];

async function fetchProjectOptionsPage(
    term: string,
    limit: number,
    sfAccountIds: readonly string[] | null,
): Promise<{ options: ProjectOption[]; total: number }> {
    const filter = {
        ...(term ? { projectName: { contains: term } } : {}),
        ...sfAccountFilter(sfAccountIds),
    };
    const data = await graphqlRequest.request<ProjectOptionsResponse>(GET_REPORT_PROJECT_OPTIONS, {
        sort: SORT,
        pagination: { limit, offset: 0 },
        ...(Object.keys(filter).length > 0 ? { filter } : {}),
    });
    const items = data?.data?.salesforceProjects?.items ?? [];
    return {
        options: items.map((project) => ({
            id: project.id,
            projectName: project.projectName,
            ref: toProjectRef(project.projectName),
            accountName: project.accountName,
            accountId: project.accountId,
            surveyIds: (project.surveys ?? [])
                .map((s) => Number.parseInt(s.id, 10))
                .filter((n) => Number.isFinite(n)),
        })),
        total: data?.data?.salesforceProjects?.total ?? 0,
    };
}

async function fetchInFieldProjectOptions(): Promise<{
    options: ProjectOption[];
    total: number;
}> {
    // "In field now": the fielding window straddles the current moment. The
    // columns are naive PST timestamps, so a UTC ISO stamp is up to 8h off —
    // immaterial against multi-day fielding windows.
    const now = new Date().toISOString();
    let surveys: InFieldSurveysResponse['surveys'];
    try {
        const data = await graphqlRequest.request<InFieldSurveysResponse>(
            GET_IN_FIELD_PROJECT_OPTIONS,
            {
                where: {
                    fieldingStartedAtPst: { lte: now },
                    fieldingEndedAtPst: { gt: now },
                },
            },
        );
        surveys = data?.data?.surveys ?? [];
    } catch (error) {
        // Some survey rows reference Salesforce project id 0; the nested
        // `salesforceProject` resolver throws on them ("SalesforceProject with
        // id 0 not found", observed on test1 2026-09-01), which turns the
        // whole response into partial-data-plus-errors and graphql-request
        // throws. The data is still there on the ClientError — use it. The
        // failed rows arrive as `salesforceProject: null` and fall out at the
        // no-resolvable-project guard below, exactly like any other orphan.
        const partial = (error as { response?: { data?: { surveys?: unknown } } })?.response?.data
            ?.surveys;
        if (!Array.isArray(partial)) throw error;
        surveys = partial as InFieldSurveysResponse['surveys'];
    }
    const byProject = new Map<string, ProjectOption>();
    for (const survey of surveys) {
        // Load-bearing, not belt-and-braces: the WHERE can't carry isActive
        // (see the note on GET_IN_FIELD_PROJECT_OPTIONS), so a paused survey
        // inside its fielding window is dropped here.
        if (!survey.isActive) continue;
        const project = survey.salesforceProject;
        const projectId = project?.id;
        const projectName = project?.projectName;
        // A live survey with no resolvable project can't be offered — the
        // planner needs the NNNNN-Name reference from the project name.
        if (!projectId || !projectName) continue;
        const surveyId = Number.parseInt(survey.id, 10);
        const existing = byProject.get(projectId);
        if (existing) {
            if (Number.isFinite(surveyId)) existing.surveyIds.push(surveyId);
            continue;
        }
        byProject.set(projectId, {
            id: projectId,
            projectName,
            ref: toProjectRef(projectName),
            accountName: project?.accountName ?? '',
            accountId: project?.accountId ?? '',
            surveyIds: Number.isFinite(surveyId) ? [surveyId] : [],
        });
    }
    // Same newest-first-by-name convention as the paged query's SORT.
    const options = [...byProject.values()].sort((a, b) =>
        b.projectName.localeCompare(a.projectName),
    );
    return { options, total: options.length };
}
